import type { ReactNode } from "react";

import {
  SITE_URL,
  baseCrumbs,
  orgNode,
  faqJsonld,
  breadcrumbJsonld,
  webPageNode,
  type LearnFAQ,
  type LearnPageData,
} from "./learnShared";

const P = (s: ReactNode): ReactNode => <p>{s}</p>;

const faqs: LearnFAQ[] = [
  {
    question: "How many antidepressants should I try before considering TMS?",
    answer:
      "FDA clearance for TMS covers adults with major depressive disorder who have not improved adequately on at least one antidepressant in the current episode. Many insurance plans require documentation of two to four failed trials. A psychiatrist can review your medication history and determine whether you meet the criteria for TMS, SAINT®, or SPRAVATO®.",
  },
  {
    question:
      "Does trying more antidepressants increase my chances of remission?",
    answer:
      "The STAR*D study found diminishing returns: roughly 37% remission after the first medication, 31% after the second, 14% after the third, and 13% after the fourth. After two or three adequate trials, the probability of remission from another medication alone decreases. This is when many psychiatrists recommend a non-medication treatment.",
  },
  {
    question: "What counts as a 'failed' antidepressant trial?",
    answer:
      "A failed trial means you took an antidepressant at a therapeutic dose for 6 to 8 weeks with no adequate improvement, or you could not reach a therapeutic dose because of side effects. A medication stopped early or never increased beyond a starting dose is usually considered an incomplete trial, not a failed one.",
  },
];

export const pageHowManyAntidepressants: LearnPageData = {
  slug: "how-many-antidepressants-should-i-try",
  title: "How Many Antidepressants Should I Try? | Neuro Wellness",
  description:
    "After two to four adequate antidepressant trials, remission odds from medication alone decrease. Learn when to consider TMS, SAINT®, or SPRAVATO®.",
  canonical: "/learn/how-many-antidepressants-should-i-try",
  h1: "How Many Antidepressants Should I Try Before Considering Another Treatment?",
  breadcrumbLabel: "How Many Antidepressants Should I Try?",
  crumbs: baseCrumbs("How Many Antidepressants Should I Try?"),
  shortAnswer:
    "FDA clearance for TMS covers adults who have not improved adequately on at least one antidepressant. Many insurance plans require two to four documented failed trials. The STAR*D study showed diminishing returns after each step — roughly 37% remission after the first medication, 31% after the second, 14% after the third. After two or three adequate trials, a psychiatrist may recommend a non-medication treatment like TMS, SAINT®, or SPRAVATO®.",
  keyTakeaways: [
    "FDA clearance for TMS requires at least one failed antidepressant trial.",
    "Many insurance plans require two to four documented trials.",
    "STAR*D showed diminishing returns after each medication step.",
    "An adequate trial means a therapeutic dose for 6 to 8 weeks.",
  ],

  sections: [
    {
      heading: "What the Research Shows",
      body: (
        <>
          {P(
            "The STAR*D study, the largest real-world antidepressant trial, followed about 3,671 adults through sequential treatment steps. Remission rates were approximately 37% after the first medication, 31% after the second, 14% after the third, and 13% after the fourth. The pattern of diminishing returns is well established.",
          )}
        </>
      ),
    },
    {
      heading: "When to Consider a Non-Medication Treatment",
      body: (
        <>
          {P(
            "After two or three adequate antidepressant trials without satisfactory improvement, many psychiatrists recommend a non-medication treatment. FDA-cleared options include TMS and SAINT® for adults with major depressive disorder who have not improved on antidepressant medication. SPRAVATO® (esketamine) is FDA-approved for treatment-resistant depression.",
          )}
          {P(
            <>
              <a
                href="/learn/what-is-treatment-resistant-depression"
                className="font-medium text-accent underline underline-offset-2 hover:opacity-80"
              >
                Learn more about treatment-resistant depression
              </a>
              .
            </>,
          )}
        </>
      ),
    },
    {
      heading: "What Insurance Typically Requires",
      body: (
        <>
          {P(
            "Most commercial insurance plans and Medicare require documentation of two to four failed antidepressant trials before authorizing TMS. Each trial must show the medication name, dose, duration, and reason for discontinuation. Your clinic verifies your benefits and helps document the history your plan requires.",
          )}
        </>
      ),
    },
  ],

  faqs,
  sources: [
    {
      citation:
        "Rush AJ, et al. Sequenced treatment alternatives to relieve depression (STAR*D). Am J Psychiatry. 2006.",
      url: "https://pubmed.ncbi.nlm.nih.gov/17074942/",
    },
  ],

  reviewBlock: {
    writtenBy: "Neuro Wellness TMS Centers of America",
    reviewedBy: "Daniel Pietras, MD",
    published: "November 3, 2026",
    lastReviewed: "November 3, 2026",
    lastUpdated: "November 3, 2026",
  },
  schemaJson: {
    "@context": "https://schema.org",
    "@graph": [
      webPageNode({
        id: `${SITE_URL}/learn/how-many-antidepressants-should-i-try#webpage`,
        name: "How Many Antidepressants Should I Try?",
        url: `${SITE_URL}/learn/how-many-antidepressants-should-i-try`,
        description:
          "When to consider TMS, SAINT, or SPRAVATO after antidepressant trials.",
      }),
      orgNode,
      faqJsonld(
        faqs,
        `${SITE_URL}/learn/how-many-antidepressants-should-i-try#faq`,
      ),
      breadcrumbJsonld(baseCrumbs("How Many Antidepressants Should I Try?")),
    ],
  },
  relatedLinks: [
    {
      title: "When Depression Treatment Isn't Working",
      href: "/depression-treatment-not-working",
      description: "Full hub with all options",
    },
    {
      title: "Can TMS Help After Antidepressants Fail?",
      href: "/learn/can-tms-help-after-antidepressants-fail",
      description: "How TMS works after medication",
    },
    {
      title: "Am I a Candidate?",
      href: "/am-i-a-candidate",
      description: "Request a consultation",
    },
  ],
};
